import type {
  Signal} from '@angular/core';
import {
  DestroyRef,
  Injectable,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Observable, Subject, filter, share } from 'rxjs';
import { io, type Socket } from 'socket.io-client';

import { AppConfigService } from '../config/app-config.service';
import { AuthStore } from '../auth/auth.store';
import type { ConnectionHealth, ConnectionStatus} from './connection-status';
import { healthOf } from './connection-status';
import type { RealtimeTopic } from './realtime-topics';

interface TopicEnvelope<T = unknown> {
  readonly topic: RealtimeTopic;
  readonly payload: T;
}

const HEARTBEAT_MS = 25_000;
const HEARTBEAT_TIMEOUT_MS = 10_000;

/**
 * Cliente WebSocket ÚNICO y multiplexado por topics (socket.io).
 *
 * Diseño (ver doc de arquitectura §Tiempo real):
 *  - Un solo socket contra el gateway Nest propio (nunca el proveedor directo).
 *  - Auth por token en el handshake; el server valida tenant + permisos por topic.
 *  - Multiplexación por topic con reference-counting: varias features comparten
 *    la suscripción; el `subscribe` al server se emite una sola vez por topic.
 *  - Reconexión con backoff exponencial + jitter (delegada a socket.io) y
 *    heartbeat aplicación-nivel con watchdog para detectar conexiones zombie.
 *  - Backpressure por `document.hidden`: se pausa el consumo con la pestaña oculta.
 *  - Los streams se exponen como Observables (para coalescing con bufferTime en
 *    los stores) y como Signals (para consumo directo en la UI).
 *
 * Anti-avalancha: este servicio NO transforma el payload; el coalescing
 * (`bufferTime(1000)` → un `patchState`/seg) vive en cada signalStore.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeConnectionService {
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);

  private socket: Socket | null = null;

  /** Bus interno: todos los mensajes de topic entran acá y se filtran por topic. */
  private readonly messages$ = new Subject<TopicEnvelope>();

  /** Reference-count de suscriptores por topic (para subscribe/unsubscribe al server). */
  private readonly topicRefs = new Map<RealtimeTopic, number>();

  private readonly _status = signal<ConnectionStatus>('idle');
  readonly status: Signal<ConnectionStatus> = this._status.asReadonly();
  readonly health: Signal<ConnectionHealth> = computed(() => healthOf(this._status()));
  readonly isConnected = computed(() => this._status() === 'connected');

  private readonly _lastError = signal<string | null>(null);
  readonly lastError: Signal<string | null> = this._lastError.asReadonly();

  /** Timestamp del último snapshot sano (para re-hidratar stores tras reconexión). */
  private readonly _reconnectedAt = signal<number | null>(null);
  readonly reconnectedAt: Signal<number | null> = this._reconnectedAt.asReadonly();

  private heartbeatTimer: ReturnType<typeof setInterval> | undefined;
  private heartbeatWatchdog: ReturnType<typeof setTimeout> | undefined;
  private paused = false;

  constructor() {
    this.destroyRef.onDestroy(() => this.disconnect());
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  /**
   * Abre la conexión (idempotente). El token va en el handshake `auth`.
   * socket.io maneja el backoff+jitter de reconexión con estos parámetros.
   */
  connect(): void {
    if (this.socket) return;

    const url = this.appConfig.config().wsUrl;
    this._status.set('connecting');

    this.socket = io(url, {
      // Polling primero + upgrade a WebSocket donde el edge lo soporte. En dev el
      // API va detrás de una API Gateway HTTP API, que NO hace proxy de WebSocket
      // (solo las WebSocket API, incompatibles con el framing de socket.io); el
      // long-polling sí pasa por ahí. Donde haya un ALB/edge con WS (prod, o dev con
      // ALB dedicado), socket.io upgradeaa solo. `['websocket']` puro dejaba el
      // realtime muerto en dev con reintentos infinitos.
      transports: ['polling', 'websocket'],
      // Token dinámico: se re-evalúa en cada (re)conexión → soporta renew.
      auth: (cb) => cb({ token: this.auth.accessToken() ?? '' }),
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 15_000,
      // randomizationFactor = jitter sobre el delay de backoff [0..1].
      randomizationFactor: 0.5,
      timeout: 10_000,
    });

    this.registerSocketHandlers(this.socket);
  }

  /** Cierra la conexión y limpia timers/heartbeat. */
  disconnect(): void {
    this.stopHeartbeat();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.socket?.disconnect();
    this.socket = null;
    this._status.set('disconnected');
    this.topicRefs.clear();
  }

  /**
   * Stream (Observable) de un topic. Ideal para stores que necesitan operadores
   * RxJS (bufferTime/throttleTime) antes de tocar el estado.
   * Hace subscribe al server en el primer consumidor y unsubscribe en el último.
   */
  on<T>(topic: RealtimeTopic): Observable<T> {
    return new Observable<T>((subscriber) => {
      this.acquireTopic(topic);

      const inner = this.messages$
        .pipe(
          filter((m) => m.topic === topic),
          share(),
        )
        .subscribe((m) => {
          if (!this.paused) subscriber.next(m.payload as T);
        });

      return () => {
        inner.unsubscribe();
        this.releaseTopic(topic);
      };
    });
  }

  /**
   * Igual que `on`, pero expone el último valor como Signal (consumo directo en
   * UI sin transformar). Se limpia con el `DestroyRef` del contexto que lo llama.
   */
  toSignal<T>(topic: RealtimeTopic, initial: T): Signal<T> {
    const state = signal<T>(initial);
    const sub = this.on<T>(topic).subscribe((value) => state.set(value));
    inject(DestroyRef).onDestroy(() => sub.unsubscribe());
    return state.asReadonly();
  }

  /** Emite un evento hacia el server (p.ej. ack de alerta). */
  emit<T>(event: string, payload: T): void {
    this.socket?.emit(event, payload);
  }

  // -- internos ------------------------------------------------------------

  private registerSocketHandlers(socket: Socket): void {
    socket.on('connect', () => {
      this._status.set('connected');
      this._lastError.set(null);
      // Re-suscribir topics vigentes tras (re)conexión.
      for (const topic of this.topicRefs.keys()) {
        socket.emit('subscribe', { topic });
      }
      this.startHeartbeat();
    });

    socket.io.on('reconnect_attempt', () => this._status.set('reconnecting'));

    socket.on('reconnect', () => {
      // Marca el momento: los stores deben pedir snapshot REST y re-enganchar.
      this._reconnectedAt.set(Date.now());
    });

    socket.on('disconnect', (reason) => {
      this.stopHeartbeat();
      this._status.set(reason === 'io client disconnect' ? 'disconnected' : 'reconnecting');
    });

    socket.on('connect_error', (err: Error) => {
      this._lastError.set(err.message);
      this._status.set('reconnecting');
    });

    // Todos los payloads de topic llegan por un canal común.
    socket.on('topic', (envelope: TopicEnvelope) => {
      this.messages$.next(envelope);
    });

    // Respuesta del heartbeat (limpia el watchdog).
    socket.on('pong', () => this.clearWatchdog());
  }

  private acquireTopic(topic: RealtimeTopic): void {
    const count = this.topicRefs.get(topic) ?? 0;
    this.topicRefs.set(topic, count + 1);
    if (count === 0 && this.socket?.connected) {
      this.socket.emit('subscribe', { topic });
    }
  }

  private releaseTopic(topic: RealtimeTopic): void {
    const count = (this.topicRefs.get(topic) ?? 1) - 1;
    if (count <= 0) {
      this.topicRefs.delete(topic);
      if (this.socket?.connected) this.socket.emit('unsubscribe', { topic });
    } else {
      this.topicRefs.set(topic, count);
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (!this.socket?.connected) return;
      this.socket.emit('ping');
      // Watchdog: si no llega pong, forzar reconexión (conexión zombie).
      this.heartbeatWatchdog = setTimeout(() => {
        this._status.set('reconnecting');
        this.socket?.disconnect().connect();
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_MS);
  }

  private clearWatchdog(): void {
    if (this.heartbeatWatchdog) {
      clearTimeout(this.heartbeatWatchdog);
      this.heartbeatWatchdog = undefined;
    }
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.clearWatchdog();
    this.heartbeatTimer = undefined;
  }

  /** Backpressure: con la pestaña oculta se pausa el consumo (no el socket). */
  private readonly onVisibilityChange = (): void => {
    this.paused = document.hidden;
  };
}
